import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

import API from "../utils/API";
import { districtLabel } from "../utils/district";
import defaultPortrait from "../assets/portrait-1.png";
import "./districtinfo.css";

const GROUPS = [
    { key: "hispanic", label: "Hispanic / Latino", color: "#e08a3c" },
    { key: "white", label: "White", color: "#458da1" },
    { key: "black", label: "Black", color: "#1a2744" },
    { key: "asian", label: "Asian", color: "#6aa84f" },
    { key: "native", label: "American Indian", color: "#a65dc4" },
    { key: "other", label: "Other / multiracial", color: "#9aa5ad" },
];

const PARTIES = [
    { key: "democratic", label: "Democratic", color: "#3a6ea5" },
    { key: "republican", label: "Republican", color: "#c4473a" },
    { key: "no_party_preference", label: "No party preference", color: "#8f9aa3" },
    { key: "american_independent", label: "American Independent", color: "#b8863b" },
    { key: "green", label: "Green", color: "#4f9d69" },
    { key: "libertarian", label: "Libertarian", color: "#d8b84a" },
    { key: "peace_and_freedom", label: "Peace and Freedom", color: "#b0577a" },
    { key: "other", label: "Other / unknown", color: "#c9d1d6" },
];

const money = (n) => `$${Math.round(n).toLocaleString()}`;
const percent = (n) => `${(n * 100).toFixed(1)}%`;

const METRICS = [
    { key: "median_income", label: "Median household income", format: money },
    { key: "poverty_rate", label: "Living below the poverty line", format: percent },
    { key: "unemployment_rate", label: "Unemployment rate", format: percent },
    { key: "bachelors_rate", label: "Adults with a bachelor's degree or higher", format: percent },
    { key: "median_home_value", label: "Median home value", format: money },
    { key: "median_rent", label: "Median monthly rent", format: money },
    { key: "homeownership_rate", label: "Homeownership rate", format: percent },
    { key: "rent_burdened_rate", label: "Renters paying 30%+ of income on rent", format: percent },
];

const INDUSTRY_LABELS = {
    agriculture: "Agriculture, forestry, fishing, mining",
    construction: "Construction",
    manufacturing: "Manufacturing",
    wholesale: "Wholesale trade",
    retail: "Retail trade",
    transportation: "Transportation, warehousing, utilities",
    information: "Information",
    finance: "Finance, insurance, real estate",
    professional: "Professional, scientific, management",
    education_health: "Education, health care, social assistance",
    hospitality: "Arts, entertainment, food service, lodging",
    other_services: "Other services",
    public_administration: "Public administration",
};

function DistrictMap({ geojson }) {
    const containerRef = useRef(null);

    useEffect(() => {
        if (!containerRef.current || !geojson) return;
        // zoomSnap 0.1 lets the fit land just inside the edges instead of rounding down to a whole zoom level.
        const map = L.map(containerRef.current, { scrollWheelZoom: false, zoomSnap: 0.1 });
        L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
            maxZoom: 18,
            // The site sends Referrer-Policy: no-referrer (helmet's default), but OpenStreetMap's tile servers require a Referer
            // and block requests without one, so the tiles opt in to sending the origin.
            referrerPolicy: "strict-origin-when-cross-origin",
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        }).addTo(map);
        const layer = L.geoJSON(geojson, {
            style: { color: "#1a2744", weight: 2, fillColor: "#458da1", fillOpacity: 0.25 },
        }).addTo(map);
        const fit = () => map.fitBounds(layer.getBounds(), { padding: [16, 16], animate: false });
        fit();
        const observer = new ResizeObserver(() => { map.invalidateSize(); fit(); });
        observer.observe(containerRef.current);
        return () => { observer.disconnect(); map.remove(); };
    }, [geojson]);

    return <div className="district-info-map" ref={containerRef} />;
}

function formatAddress(address) {
    if (!address) return null;
    const cityLine = [address.city, [address.state, address.zip].filter(Boolean).join(" ")].filter(Boolean).join(", ");
    return (
        <>
            {address.street}
            {cityLine && <><br />{cityLine}</>}
        </>
    );
}

function Office({ label, address, phone }) {
    return (
        <div className="district-info-office">
            <div className="district-info-office-label">{label}</div>
            {address ? <div>{formatAddress(address)}</div> : <div className="district-info-empty">No address on file.</div>}
            {phone && <div><a href={`tel:${phone.replace(/[^\d+]/g, "")}`}>{phone}</a></div>}
        </div>
    );
}

// The sitting member: photo, name, party, contact details, and the Capitol and district offices on file.
function Member({ member }) {
    const website = member.website && (member.website.startsWith("http") ? member.website : `https://${member.website}`);
    return (
        <div className="district-info-card">
            <div className="district-info-card-header">{member.title || "Representative"}</div>
            <div className="district-info-member">
                <img
                    className="district-info-member-photo"
                    src={member.profile_picture || defaultPortrait}
                    alt={`${member.first_name} ${member.last_name}`}
                />
                <div className="district-info-member-main">
                    <Link to={`/profile/${member.profile_id}`} className="district-info-member-name">
                        {member.first_name} {member.last_name}
                    </Link>
                    {member.party && <div className="district-info-member-party">{member.party}</div>}
                    <div className="district-info-member-contact">
                        {member.email && <div><i className="bi bi-envelope" /> <a href={`mailto:${member.email}`}>{member.email}</a></div>}
                        {website && <div><i className="bi bi-globe" /> <a href={website} target="_blank" rel="noopener noreferrer">{member.website.replace(/^https?:\/\//, "")}</a></div>}
                    </div>
                </div>
            </div>
            <div className="district-info-offices">
                {member.capitol_offices.map(office => (
                    <Office
                        key={`${office.address?.street}|${office.phone}`}
                        label="Capitol Office"
                        address={office.address}
                        phone={office.phone}
                    />
                ))}
                {member.district_offices.map(office => (
                    <Office key={`${office.address.street}|${office.address.city}`} label="District Office" address={office.address} phone={office.phone} />
                ))}
            </div>
            {member.district_offices.length === 0 && (
                <p className="district-info-note mt-2">No district offices are on file yet.</p>
            )}
        </div>
    );
}

function NameChips({ items }) {
    return (
        <div className="district-info-chips">
            {items.map(item => (
                <span key={item.name} className="district-info-chip">
                    {item.name}{!item.whole && <span className="district-info-chip-part"> (part)</span>}
                </span>
            ))}
        </div>
    );
}

export function Demographics({ data }) {
    const rows = GROUPS.filter(g => data[g.key] != null && (g.key !== "native" || data.native > 0));
    const total = data.cvap || 0;
    if (!total) return null;
    const pct = (n) => (n / total) * 100;
    return (
        <>
            <div className="district-info-stats">
                {data.population != null && (
                    <div><strong>{data.population.toLocaleString()}</strong><span>Population</span></div>
                )}
                <div><strong>{total.toLocaleString()}</strong><span>Citizen voting-age population</span></div>
            </div>
            <div className="district-info-bar" role="img" aria-label="Citizen voting-age population by race and ethnicity">
                {rows.map(g => (
                    <span key={g.key} style={{ width: `${pct(data[g.key])}%`, background: g.color }} title={`${g.label}: ${pct(data[g.key]).toFixed(1)}%`} />
                ))}
            </div>
            <ul className="district-info-legend">
                {rows.map(g => (
                    <li key={g.key}>
                        <span className="district-info-swatch" style={{ background: g.color }} />
                        <span className="district-info-legend-label">{g.label}</span>
                        <span className="district-info-legend-value">{pct(data[g.key]).toFixed(1)}%</span>
                    </li>
                ))}
            </ul>
            <p className="district-info-note">
                Share of citizens 18 and older, from the 2019 estimates in the Citizens Redistricting Commission's final map data.
            </p>
        </>
    );
}

export function Registration({ data }) {
    const total = data.total || 0;
    if (!total) return null;
    const pct = (n) => (n / total) * 100;
    const rows = PARTIES.filter(p => data[p.key] > 0);
    return (
        <>
            <div className="district-info-stats">
                <div><strong>{total.toLocaleString()}</strong><span>Registered voters</span></div>
            </div>
            <div className="district-info-bar" role="img" aria-label="Registered voters by party">
                {rows.map(p => (
                    <span key={p.key} style={{ width: `${pct(data[p.key])}%`, background: p.color }} title={`${p.label}: ${pct(data[p.key]).toFixed(1)}%`} />
                ))}
            </div>
            <ul className="district-info-legend">
                {rows.map(p => (
                    <li key={p.key}>
                        <span className="district-info-swatch" style={{ background: p.color }} />
                        <span className="district-info-legend-label">{p.label}</span>
                        <span className="district-info-legend-value">{pct(data[p.key]).toFixed(1)}%</span>
                        <span className="district-info-legend-count">{data[p.key].toLocaleString()}</span>
                    </li>
                ))}
            </ul>
            <p className="district-info-note">
                From the Secretary of State's Report of Registration{data.as_of ? `, as of ${data.as_of}` : ""}.
            </p>
        </>
    );
}

export function Economy({ data, state }) {
    const metrics = METRICS.filter(m => data[m.key] != null);
    const industries = (data.industries || []).filter(i => i.share != null).sort((a, b) => b.share - a.share).slice(0, 6);
    if (!metrics.length) return null;
    const top = industries.length ? industries[0].share : 1;
    return (
        <>
            <div className="district-info-metrics">
                {metrics.map(m => (
                    <div key={m.key} className="district-info-metric">
                        <strong>{m.format(data[m.key])}</strong>
                        <span>{m.label}</span>
                        {state && state[m.key] != null && <em>California: {m.format(state[m.key])}</em>}
                    </div>
                ))}
            </div>
            {industries.length > 0 && (
                <>
                    <h6 className="district-info-subheader">Where residents work</h6>
                    <ul className="district-info-industries">
                        {industries.map(i => (
                            <li key={i.key}>
                                <div className="district-info-industry-row">
                                    <span>{INDUSTRY_LABELS[i.key] || i.key}</span>
                                    <span className="district-info-legend-value">{percent(i.share)}</span>
                                </div>
                                <div className="district-info-industry-track">
                                    <span style={{ width: `${(i.share / top) * 100}%` }} />
                                </div>
                            </li>
                        ))}
                    </ul>
                </>
            )}
            <p className="district-info-note">
                American Community Survey 5-year estimates, 2020–2024, from the Census Bureau. Industries are those of employed residents, wherever they work.
            </p>
        </>
    );
}

export default function DistrictInfo({ district, showMember = false }) {
    const [info, setInfo] = useState(null);
    const [status, setStatus] = useState("loading");

    useEffect(() => {
        let cancelled = false;
        setStatus("loading");
        setInfo(null);
        API.getDistrictInfo(district)
            .then(res => { if (!cancelled) { setInfo(res.data); setStatus("ready"); } })
            .catch(() => { if (!cancelled) setStatus("error"); });
        return () => { cancelled = true; };
    }, [district]);

    if (status === "loading") {
        return (
            <div className="district-info-loading">
                <div className="spinner-border loading-spinner" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }
    if (status === "error" || !info) {
        return <p className="district-info-empty">District information isn't available for {districtLabel(district)}.</p>;
    }

    return (
        <div className="district-info">
            <h5 className="district-info-title">{districtLabel(district)}</h5>

            <div className="district-info-layout">
            <div className="district-info-left">
            {showMember && (info.member
                ? <Member member={info.member} />
                : <div className="district-info-card"><p className="district-info-empty">No current member on file for this district.</p></div>
            )}

            <div className="district-info-card">
                <div className="district-info-card-header">Counties</div>
                {info.counties.length ? <NameChips items={info.counties} /> : <p className="district-info-empty">None listed.</p>}
            </div>

            <div className="district-info-card">
                <div className="district-info-card-header">Cities</div>
                {info.cities.length ? <NameChips items={info.cities} /> : <p className="district-info-empty">No incorporated cities.</p>}
                {info.communities.length > 0 && (
                    <details className="district-info-communities">
                        <summary>Unincorporated communities ({info.communities.length})</summary>
                        <NameChips items={info.communities} />
                    </details>
                )}
            </div>

            {info.registration && (
                <div className="district-info-card">
                    <div className="district-info-card-header">Voter Registration</div>
                    <Registration data={info.registration} />
                </div>
            )}

            {info.demographics && (
                <div className="district-info-card">
                    <div className="district-info-card-header">Demographics</div>
                    <Demographics data={info.demographics} />
                </div>
            )}

            {info.economy && (
                <div className="district-info-card">
                    <div className="district-info-card-header">Economy &amp; Housing</div>
                    <Economy data={info.economy} state={info.state_economy} />
                </div>
            )}

            </div>

            <div className="district-info-right">
                <div className="district-info-card district-info-map-card">
                    <DistrictMap geojson={info.geojson} />
                </div>
            </div>
            </div>
        </div>
    );
}
